"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Запас до вьюпорта, при котором начинаем монтировать. */
  rootMargin?: string;
  /** Резерв высоты, чтобы отложенный монтаж не давал скачка вёрстки. */
  minHeight?: number;
  /** Классы обёртки: нужны, когда она должна занимать размеры родителя. */
  className?: string;
  /**
   * Якорь секции вешаем на ВНЕШНЮЮ обёртку, которая отрендерена всегда. Если оставить
   * id внутри children, то до подъезда к вьюпорту элемента `#id` в DOM нет и клик по
   * пункту меню никуда не скроллит.
   */
  id?: string;
};

/**
 * Откладывает монтирование тяжёлых below-the-fold секций (Swiper-галереи, форма) до
 * момента, когда они подъезжают к вьюпорту. Их JS не исполняется в окне гидрации
 * хироя, главный поток свободен под LCP - это основной остаток TBT на мобиле.
 *
 * Для SEO безопасно: заголовок, описание и вся текстовая часть, критичная для
 * индексации, отрендерены на сервере вне этих секций.
 */
export function DeferHydrate({ children, rootMargin = "300px", minHeight, id, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;

    // Очень старый браузер без IntersectionObserver - показываем сразу.
    if (typeof IntersectionObserver === "undefined") {
      queueMicrotask(() => setShow(true));
      return;
    }

    let idle: number | undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        // Монтируем в простое главного потока. На широком экране под rootMargin
        // сразу попадает несколько тяжёлых секций, и синхронный монтаж всех разом
        // давал всплеск TBT до 600 мс. requestIdleCallback разносит работу по кадрам,
        // timeout гарантирует монтаж даже на постоянно занятом потоке.
        if (typeof requestIdleCallback === "function") {
          idle = requestIdleCallback(() => setShow(true), { timeout: 400 });
        } else {
          setShow(true);
        }
      },
      { rootMargin }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (idle !== undefined && typeof cancelIdleCallback === "function") {
        cancelIdleCallback(idle);
      }
    };
  }, [show, rootMargin]);

  return (
    <div
      ref={ref}
      id={id}
      className={className}
      style={!show && minHeight ? { minHeight } : undefined}
    >
      {show ? children : null}
    </div>
  );
}
